import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { taxZoneEndpoints } from '@/lib/api/endpoints';
import { isCaProvinceCode } from '@/lib/postal-code';
import { taxZoneKeys, taxGroupKeys } from '@/lib/queryKeys';
import type { TaxGroup, TaxZone, TaxZoneLookupResult } from '@/types/tax';
import { toast } from 'sonner';

function upsertByIdOrZip<T extends { id?: string; zipCode?: string }>(
  current: T[] | undefined,
  next: T,
): T[] {
  if (!current) return [next];
  const index = current.findIndex(
    (item) => (next.id && item.id === next.id) || (next.zipCode && item.zipCode === next.zipCode),
  );
  if (index === -1) return [...current, next];
  return current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...next } : item));
}

function upsertById<T extends { id: string }>(current: T[] | undefined, next: T): T[] {
  if (!current) return [next];
  return current.some((item) => item.id === next.id)
    ? current.map((item) => (item.id === next.id ? { ...item, ...next } : item))
    : [...current, next];
}

export function useLookupTaxZone() {
  const qc = useQueryClient();

  return useApiMutation<TaxZoneLookupResult, { zipCode: string }>({
    mutationFn: (data) =>
      apiPost<TaxZoneLookupResult>(operationsClient, taxZoneEndpoints.lookup(), data),
    onSuccess: (data) => {
      qc.setQueryData<TaxZone[]>(taxZoneKeys.list(), (current) => upsertByIdOrZip(current, data));
      const taxGroup = data.taxGroup;
      if (taxGroup) {
        qc.setQueryData<TaxGroup[]>(taxGroupKeys.list(), (current) =>
          upsertById(current, taxGroup),
        );
      }
      void qc.invalidateQueries({ queryKey: taxZoneKeys.all() });
      void qc.invalidateQueries({ queryKey: taxGroupKeys.all() });
      // Use FSA/ZIP based on the response's stateCode — Canadian
      // province codes are CA-shaped (ON, QC, AB, …) per
      // `isCaProvinceCode`. Pre 2026-06-16 the toast hardcoded "ZIP",
      // which read as a translation bug when the user looked up an FSA.
      const isCa = isCaProvinceCode(data.stateCode);
      toast.success(`${isCa ? 'FSA' : 'ZIP'} zone created: ${data.zipCode}`);
    },
    onError: (err) => {
      // BE attaches a stable `code` so we can pick a user-friendly message
      // instead of leaking raw Ziptax errors like "rCode=110".
      const body = (
        err as { response?: { data?: { code?: string; message?: string; zip?: string } } }
      )?.response?.data;
      const code = body?.code;
      const fallback =
        body?.message ?? (err instanceof Error ? err.message : 'Failed to look up ZIP');

      let userMessage = fallback;
      if (code === 'TAX_ZIP_NOT_FOUND') {
        userMessage = body?.zip
          ? `ZIP ${body.zip} isn't a recognized US ZIP code. Please double-check it and try again.`
          : "That ZIP code isn't recognized. Please double-check it and try again.";
      } else if (code === 'TAX_SERVICE_UNAVAILABLE') {
        userMessage =
          'Tax lookup is temporarily unavailable. Please try again in a moment, or contact support if this keeps happening.';
      }

      toast.error(userMessage);
    },
  });
}
